/**
 * Project Team Management Page
 * 
 * Manages both permission-based roles (Editor/Viewer) and functional roles
 * (Financial Planner, Engineer, etc.) for projects.
 * 
 * Features:
 * - Side-by-side display of permission roles AND functional assignments
 * - Add/remove team members with functional roles
 * - Configure available functional role types (Admin only)
 * - Clear labeling to distinguish permission vs functional roles
 */

import React, { useState, useEffect } from 'react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import TextArea from '../../components/TextArea';
import Badge from '../../components/Badge';
import TabNavigation, { Tab } from '../../components/TabNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getTeamMembers, 
  createTeamMember, 
  deleteTeamMember 
} from '../../api/team-members';
import { 
  getFunctionalRoles, 
  createFunctionalRole,
  updateFunctionalRole,
  deleteFunctionalRole 
} from '../../api/functional-roles';
import { getProjects } from '../../api/projects';
import { getAllUsers } from '../../api/users';
import type { ProjectTeamMember, FunctionalRole, ProjectTeamMemberCreate, FunctionalRoleCreate } from '../../types/team';
import type { Project } from '../../types/project';
import type { User } from '../../types/user';

const ProjectTeamPage: React.FC = () => {
  const { isAdmin } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState<'assignments' | 'roles'>('assignments');
  const [teamMembers, setTeamMembers] = useState<ProjectTeamMember[]>([]);
  const [functionalRoles, setFunctionalRoles] = useState<FunctionalRole[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state for team member
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [newMember, setNewMember] = useState<ProjectTeamMemberCreate>({
    user_id: '',
    project_id: '',
    functional_role: '',
    start_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Form state for functional role
  const [showAddRoleForm, setShowAddRoleForm] = useState(false);
  const [newRole, setNewRole] = useState<FunctionalRoleCreate>({
    name: '',
    description: '',
    is_active: true
  });
  
  // Filter state
  const [filterProject, setFilterProject] = useState<string>('');
  
  // Load data
  useEffect(() => {
    loadAllData();
  }, []);
  
  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [membersData, rolesData, projectsData, usersData] = await Promise.all([
        getTeamMembers(),
        getFunctionalRoles(),
        getProjects(),
        getAllUsers()
      ]);
      
      setTeamMembers(membersData);
      setFunctionalRoles(rolesData);
      setProjects(projectsData);
      setUsers(usersData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper functions
  const getUserName = (userId: string): string => {
    const user = users.find(u => u.id === userId);
    return user ? (user.full_name || user.username) : `User #${userId}`;
  };
  
  const getProjectName = (projectId: string): string => {
    if (!projects || !Array.isArray(projects)) return `Project #${projectId}`;
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : `Project #${projectId}`;
  };
  
  // Team Member Management
  const handleCreateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMember.user_id || !newMember.project_id || !newMember.functional_role) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      await createTeamMember(newMember);
      await loadAllData();
      setShowAddMemberForm(false);
      setNewMember({
        user_id: '',
        project_id: '',
        functional_role: '',
        start_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create assignment';
      setError(errorMessage);
    }
  };
  
  const handleDeleteMember = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to remove this team member assignment?')) {
      return;
    }
    
    try {
      await deleteTeamMember(assignmentId);
      await loadAllData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete assignment';
      setError(errorMessage);
    }
  };
  
  // Functional Role Management (Admin only)
  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newRole.name) {
      setError('Role name is required');
      return;
    }
    
    try {
      await createFunctionalRole(newRole);
      await loadAllData();
      setShowAddRoleForm(false);
      setNewRole({ name: '', description: '', is_active: true });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create role';
      setError(errorMessage);
    }
  };
  
  const handleToggleRoleActive = async (role: FunctionalRole) => {
    if (role.is_system) {
      setError('Cannot modify system-defined roles');
      return;
    }
    
    try {
      await updateFunctionalRole(role.id, { is_active: !role.is_active });
      await loadAllData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update role';
      setError(errorMessage);
    }
  };
  
  const handleDeleteRole = async (roleId: string, roleName: string) => {
    if (!confirm(`Are you sure you want to delete the "${roleName}" role type?`)) {
      return;
    }
    
    try {
      await deleteFunctionalRole(roleId);
      await loadAllData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete role';
      setError(errorMessage);
    }
  };
  
  // Filter team members
  const filteredMembers = filterProject
    ? teamMembers.filter(m => m.project_id === filterProject)
    : teamMembers;

  const tabs: Tab[] = [
    { id: 'assignments', label: 'Team Assignments' },
    ...(isAdmin ? [{ id: 'roles', label: 'Team Function Types' }] : []),
  ];

  if (loading) {
    return (
      <div className="loading-container min-h-screen">
        <div className="spinner" />
      </div>
    );
  }
  
  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Team Functions
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Assign job functions (Financial Planner, Engineer, etc.) to users on projects
          </p>
        </div>
      </div>
      
      {/* Error Alert */}
      {error && (
        <div className="rounded-lg px-4 py-3 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
          <p>{error}</p>
          <button 
            onClick={() => setError(null)}
            className="text-sm underline mt-2"
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Info Card */}
      <Card variant="outlined">
        <Card.Body>
          <div className="flex items-start space-x-4">
            <div className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 p-3 rounded-lg">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Understanding Team Functions
              </h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                <li>• <strong>Team Functions</strong> describe a person's job on a project (Financial Planner, Engineer, etc.)</li>
                <li>• They are <strong>descriptive labels only</strong> — they do not grant any access</li>
                <li>• Access is controlled separately by the system role and Editor/Viewer grants (see Manage Access)</li>
                <li>• A user can have multiple team functions on the same project</li>
              </ul>
            </div>
          </div>
        </Card.Body>
      </Card>
      
      {/* Tabs */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as 'assignments' | 'roles')}
      />
      
      {/* Tab Content */}
      {activeTab === 'assignments' && (
        <div className="space-y-6">
          {/* Filters and Actions */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="w-full sm:w-64">
              <Select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Select>
            </div>
            
            {filterProject && (
              <Button variant="outline" onClick={() => setFilterProject('')}>
                Clear Filter
              </Button>
            )}
            
            <Button 
              variant="primary"
              onClick={() => setShowAddMemberForm(!showAddMemberForm)}
            >
              {showAddMemberForm ? 'Cancel' : 'Add Team Member'}
            </Button>
          </div>
          
          {/* Add Member Form */}
          {showAddMemberForm && (
            <Card>
              <Card.Header title="Assign Team Function" />
              <Card.Body>
                <form onSubmit={handleCreateMember} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="User"
                      required
                      value={newMember.user_id}
                      onChange={(e) => setNewMember({ ...newMember, user_id: e.target.value })}
                    >
                      <option value="">Select User</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name || user.username} ({user.email})
                        </option>
                      ))}
                    </Select>

                    <Select
                      label="Project"
                      required
                      value={newMember.project_id}
                      onChange={(e) => setNewMember({ ...newMember, project_id: e.target.value })}
                    >
                      <option value="">Select Project</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </Select>

                    <Select
                      label="Team Function"
                      required
                      value={newMember.functional_role}
                      onChange={(e) => setNewMember({ ...newMember, functional_role: e.target.value })}
                    >
                      <option value="">Select Role</option>
                      {functionalRoles.filter(r => r.is_active).map(role => (
                        <option key={role.id} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                    </Select>

                    <Input
                      type="date"
                      label="Start Date"
                      value={newMember.start_date || ''}
                      onChange={(e) => setNewMember({ ...newMember, start_date: e.target.value })}
                    />
                  </div>

                  <TextArea
                    label="Notes"
                    rows={3}
                    value={newMember.notes || ''}
                    onChange={(e) => setNewMember({ ...newMember, notes: e.target.value })}
                    placeholder="Optional notes about this assignment..."
                  />
                  
                  <div className="flex gap-2">
                    <Button type="submit" variant="primary">
                      Assign Role
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddMemberForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card.Body>
            </Card>
          )}
          
          {/* Team Members List */}
          <div className="grid gap-4">
            {filteredMembers.length === 0 ? (
              <Card>
                <Card.Body>
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    {filterProject 
                      ? 'No team members assigned to this project'
                      : 'No team members assigned yet. Click "Add Team Member" to get started.'}
                  </p>
                </Card.Body>
              </Card>
            ) : (
              filteredMembers.map(member => (
                <Card key={member.id} variant="elevated">
                  <Card.Body>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {getUserName(member.user_id)}
                          </h3>
                          <span className="px-3 py-1 bg-accent-100 dark:bg-accent-900/30 text-accent-800 dark:text-accent-300 text-sm font-medium rounded-full">
                            {member.functional_role}
                          </span>
                        </div>
                        
                        <p className="text-gray-700 dark:text-gray-300 mb-2">
                          <strong>Project:</strong> {getProjectName(member.project_id)}
                        </p>
                        
                        {member.start_date && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong>Start Date:</strong> {new Date(member.start_date).toLocaleDateString()}
                          </p>
                        )}
                        
                        {member.notes && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                            <strong>Notes:</strong> {member.notes}
                          </p>
                        )}
                        
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                          Assigned {new Date(member.assigned_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <Button 
                        variant="danger"
                        onClick={() => handleDeleteMember(member.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
      
      {/* Functional Roles Tab (Admin Only) */}
      {activeTab === 'roles' && isAdmin && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-gray-600 dark:text-gray-400">
              Manage available team function types that can be assigned to users
            </p>
            <Button
              variant="primary"
              onClick={() => setShowAddRoleForm(!showAddRoleForm)}
            >
              {showAddRoleForm ? 'Cancel' : 'Add Function Type'}
            </Button>
          </div>
          
          {/* Add Role Form */}
          {showAddRoleForm && (
            <Card>
              <Card.Header title="Create Team Function Type" />
              <Card.Body>
                <form onSubmit={handleCreateRole} className="space-y-4">
                  <Input
                    type="text"
                    label="Role Name"
                    required
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                    placeholder="e.g., Quality Assurance Lead"
                  />

                  <TextArea
                    label="Description"
                    rows={3}
                    value={newRole.description || ''}
                    onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                    placeholder="Describe this role's responsibilities..."
                  />
                  
                  <div className="flex gap-2">
                    <Button type="submit" variant="primary">
                      Create Function Type
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddRoleForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card.Body>
            </Card>
          )}
          
          {/* Roles List */}
          <div className="grid gap-4">
            {functionalRoles.map(role => (
              <Card key={role.id} variant="outlined">
                <Card.Body>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {role.name}
                        </h3>
                        {role.is_system && (
                          <Badge variant="info" size="sm">System</Badge>
                        )}
                        <Badge variant={role.is_active ? 'success' : 'default'} size="sm">
                          {role.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      
                      {role.description && (
                        <p className="text-gray-700 dark:text-gray-300">
                          {role.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      {!role.is_system && (
                        <>
                          <Button 
                            variant="outline"
                            onClick={() => handleToggleRoleActive(role)}
                          >
                            {role.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button 
                            variant="danger"
                            onClick={() => handleDeleteRole(role.id, role.name)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTeamPage;
